import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';
import { GlobalService } from '../services/global.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { SectorService } from '../services/sector.service';
import { ToastService } from 'services/toast.service';

import { ROUTES, SECTOR_ID } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Client, CommonGlobalService, MultiSelect, Option, RootState, Sector, Site, UserFilter, UserLinked } from '@umanforge/common';
import { ModifySector } from 'interfaces/api_sector.interfaces';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Select } from '../components/inputs/select.component';
import { Input } from '../components/inputs/input.component';
import { Loading } from '../components/loading.component';
import { SwitchAdminUser } from 'components/users/switch_admin_user.component';
import { ErrorModal } from 'components/modals/error_modal.component';
import { BodyConfirmModal } from 'components/modals/body/body_confirm_modal.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { BinIcon } from '../components/icons/bin_icon.component';
import { SectorsIcon } from '../components/icons/sectors_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';

const toastService: ToastService = new ToastService( 'Details Sector' );
const defaultChangedUsers = { added: { user: [], admin: [] }, removed: { user: [], admin: [] } };


export function DetailsSectorsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ sector, setSector ] = useState<Sector | null>( null );
  const [ siteOfSector, setSiteOfSector ] = useState<Option<Site> | undefined>();
  const [ clientOfSite, setClientOfSite ] = useState<Option<Client> | undefined>();

  const [ modifySector, setModifySector ] = useState<ModifySector>( {} );
  const [ changedUsers, setChangedUsers ] = useState<{ added: { user: UserLinked[], admin: UserLinked[] }, removed: { user: UserLinked[], admin: UserLinked[] } }>( defaultChangedUsers );
  const [ defaultUsers, setDefaultUsers ] = useState<Option<UserLinked>[]>( [] );
  const [ selectedUsers, setSelectedUsers ] = useState<Option<UserLinked>[]>( [] );

  const [ canPut, setCanPut ] = useState<boolean>( false );
  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ isAdminOfSector, setIsAdminOfSector ] = useState<boolean>( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshSector();
    }
  }, [ me ] );

  useEffect( () => {
    if ( sector ) {
      checkCanPutSector();
    }
  }, [ modifySector, changedUsers ] );

  useEffect( () => {
    const items = {
      added: {
        admin: selectedUsers
          .map( user => { return user.object; } )
          .filter( user => {
            return (
              ( !defaultUsers.find( sectorUser => { return ( sectorUser.object.id === user.id ); } ) && user.isAdmin === true ) ||
              ( defaultUsers.find( sectorUser => { return ( sectorUser.object.id === user.id && ( sectorUser.object.isAdmin === false || sectorUser.object.isAdmin == null ) ) && user.isAdmin === true; } ) )
            );
          } ),
        user: selectedUsers
          .map( user => { return user.object; } )
          .filter( user => {
            return ( !defaultUsers.find( sectorUser => { return ( sectorUser.object.id === user.id ); } ) && ( user.isAdmin === false || user.isAdmin == null ) );
          } )
      },
      removed: {
        admin: defaultUsers
          .map( sectorUser => { return sectorUser.object; } )
          .filter( sectorUser => {
            return selectedUsers.find( user => { return ( user.object.id === sectorUser.id && ( user.object.isAdmin === false || user.object.isAdmin == null ) ); } ) && sectorUser.isAdmin === true;
          } ),
        user: defaultUsers
          .map( sectorUser => { return sectorUser.object; } )
          .filter( sectorUser => {
            return !selectedUsers.find( user => { return ( user.object.id === sectorUser.id ); } );
          } )
      }
    };
    setChangedUsers( items );
  }, [ selectedUsers ] );

  /*------------------------- Requetes API User -------------------------*/
  const refreshSector = async () => {
    setSector( null );
    setIsLoading( true );
    if ( params.sectorId ) {
      const refreshedSector = await SectorService.getSector( params.sectorId, token, dispatch, () => { NavigationService.toSectors( navigate, false ); } );
      const refreshedSite = await SiteService.getSite( refreshedSector.siteId, token, dispatch, () => { NavigationService.toSectors( navigate, false ); } );
      const refreshedClient = await ClientService.getClient( refreshedSite.clientId, token, dispatch, () => { NavigationService.toSectors( navigate, false ); } );
      const isAdmin: boolean = ( me != null && ( CommonGlobalService.isAdminOfSector( me, refreshedSector.id ) || CommonGlobalService.isAdminOfSite( me, refreshedSite.id ) || CommonGlobalService.isAdminOfClient( me, refreshedClient.id ) ) );
      setIsAdminOfSector( isAdmin );
      Promise.all( [
        UserService.getUsersOfSector( params.sectorId, token, dispatch, () => { NavigationService.toSectors( navigate, false ); } ),
        UserService.getAdminsOfSector( params.sectorId, token, dispatch, () => { NavigationService.toSectors( navigate, false ); } ),
      ] )
        .then( responses => {
          const admins = ( responses[ 1 ] ?? [] ).map( user => { return GlobalService.toUserLinked( user, true ); } );
          const users = ( responses[ 0 ] ?? [] ).map( user => { return GlobalService.toUserLinked( user, false ); } )
            .filter( user => { return admins.find( admin => { return user.id === admin.id; } ) == null; } );
          const clientUsers = CommonGlobalService.toOptions( admins.concat( users ), isAdmin, !isAdmin )
            .map( user => {
              const admin = ( responses[ 1 ] ?? [] ).find( adm => { return adm.id === user.object.id; } );
              let isHigherAdmin = false;
              if ( admin ) {
                const clientOfUser = admin.clients.find( client => { return client.id === refreshedClient.id; } );
                const siteOfUser = clientOfUser?.sites?.find( site => { return site.id === refreshedSite.id; } );
                isHigherAdmin = ( clientOfUser != null && clientOfUser.isAdmin == true ) || ( siteOfUser != null && siteOfUser.isAdmin == true );
              }

              return {
                ...user,
                isDisabled: isHigherAdmin
              };
            } );

          setDefaultUsers( structuredClone( clientUsers ) );
          setSelectedUsers( structuredClone( clientUsers ) );

          setSector( refreshedSector );
          setSiteOfSector( CommonGlobalService.toOptions( [ refreshedSite ] )[ 0 ] );
          setClientOfSite( CommonGlobalService.toOptions( [ refreshedClient ] )[ 0 ] );
        } )
        .finally( () => {
          setIsLoading( false );
        } );
    }
  };

  const deleteSector = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      setIsLoading( true );
      let isDeleted = false;
      if ( params.sectorId ) {
        isDeleted = await SectorService.deleteSector( params.sectorId, token, dispatch, refreshSector, refreshSector );
      }
      if ( isDeleted ) {
        toastService.showInfo( t( 'The {0} sector has been delete.' ).replace( '{0}', ( sector?.name ?? t( 'The sector' ) ) ) );
        NavigationService.toSectors( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putSector = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isSectorChange: boolean =  modifySector.name != null && modifySector.name != '' && modifySector.name != sector?.name;
      if ( params.sectorId && isSectorChange ) {
        isUpdated = await SectorService.putSector( params.sectorId, modifySector, token, dispatch, refreshSector, refreshSector );
      }
      resolve( isUpdated );
    } );
  };

  const addUsersToSector = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( sector?.id && changedUsers.added.user.length > 0 ) {
        const body = { users: changedUsers.added.user.map( user => { return user.id; } ) };
        isUpdated = await SectorService.postUsersToSector( sector?.id, body, token, dispatch, refreshSector, refreshSector );
      }
      resolve( isUpdated );
    } );
  };

  const removedUsersToSector = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( sector?.id && changedUsers.removed.user.length > 0 ) {
        const body = { users: changedUsers.removed.user.map( user => { return user.id; } ) };
        isUpdated = await SectorService.deleteUsersToSector( sector?.id, body, token, dispatch, refreshSector, refreshSector );
      }
      resolve( isUpdated );
    } );
  };

  const addAdminsToSector = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( sector?.id && changedUsers.added.admin.length > 0 ) {
        const body = { admins: changedUsers.added.admin.map( admin => { return admin.id; } ) };
        isUpdated = await SectorService.postAdminsToSector( sector?.id, body, token, dispatch, refreshSector, refreshSector );
      }
      resolve( isUpdated );
    } );
  };

  const removedAdminsToSector = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( sector?.id && changedUsers.removed.admin.length > 0 ) {
        const body = { admins: changedUsers.removed.admin.map( admin => { return admin.id; } ) };
        isUpdated = await SectorService.deleteAdminsToSector( sector?.id, body, token, dispatch, refreshSector, refreshSector );
      }
      resolve( isUpdated );
    } );
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    if ( siteOfSector != null ) {
      filter.filterSites = [ siteOfSector.value ];
    }

    const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
    return { total: result.total, list: CommonGlobalService.toOptions<UserLinked>(
      result.users.map<UserLinked>( user => {
        return {
          id: user.id,
          fullname: user.fullname,
          isDisable: user.isDisable,
          isAdminOfUser: user.isAdminOfUser,
          isAdmin: selectedUsers.find( selectedUser => { return selectedUser.value === user.id; } )?.object.isAdmin ?? false,
        };
      } ) )
    };
  };

  /*---------------------------- Functions ------------------------------*/
  const onSaveClick = async () => {
    let isUserModify = false;
    setIsLoading( true );
    if ( sector ) {
      const isPutSector = await putSector();
      const isAddUsersToSector = await addUsersToSector();
      const isAddAdminsToSector = await addAdminsToSector();
      const isRemovedAdminsToSector = await removedAdminsToSector();
      const isRemovedUsersToSector = await removedUsersToSector();
      isUserModify = isPutSector ||
        isAddUsersToSector ||
        isAddAdminsToSector ||
        isRemovedAdminsToSector ||
        isRemovedUsersToSector;
    }
    if ( isUserModify ) {
      toastService.showInfo( t( 'The {0} sector has been modify.' ).replace( '{0}', ( sector?.name ?? t( 'The sector' ) ) ) );
      refreshSector();
    }
    setIsLoading( false );
  };

  const checkCanPutSector = () => {
    const requirement = ( modifySector.name != '' );
    const isSectorChange: boolean =  modifySector.name != null && modifySector.name != sector?.name;
    const isUsersChange: boolean = changedUsers.added.user.length > 0 || changedUsers.added.admin.length > 0 || changedUsers.removed.user.length > 0 || changedUsers.removed.admin.length > 0;
    setCanPut(
      requirement && (
        isSectorChange ||
        isUsersChange
      )
    );
    setHasChanged(
      isSectorChange ||
      isUsersChange
    );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChanged } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Modify sector' ) } needConfirmationToNav={ hasChanged }/>
        <Breadcrumb
          needConfirmationToNav={ hasChanged }
          links={
            [
              { label: t( 'common:sector' ), route: ROUTES.SECTORS, icon: ( <ClientsIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: sector ? sector.name : t( 'Modify sector' ), route: ROUTES.SECTOR.replace( SECTOR_ID, params.sectorId ?? '' ) }
            ]
          }
        />
        {
          !isLoading && sector ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ clientOfSite }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ [ clientOfSite ] }
                  />
                </div>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ siteOfSector }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ [ siteOfSector ] }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_SECTOR_NAME }
                    label={ t( 'Sector name' ) }
                    disabled={ !isAdminOfSector }
                    placeholder={ t( 'Sector' ) + '...' }
                    icon={ <SectorsIcon className='w-7 h-7' /> }
                    value={ sector.name ?? ''}
                    onChange={ newSectorName => {
                      modifySector.name = sector?.name != newSectorName ? newSectorName : undefined;
                      setModifySector( structuredClone( modifySector ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'Users' ) + ' (' + selectedUsers.length + ')' }
                    disabled={ !isAdminOfSector }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedUsers }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedUsers( options );
                    } }
                    extra={ selectedValue => {
                      return (
                        <SwitchAdminUser
                          objectId={ selectedValue.value }
                          isDisabled={ selectedValue.isDisabled }
                          defaultValue={ selectedValue.object.isAdmin }
                          onChange={ value => {
                            selectedValue.object.isAdmin = value;
                            const findedUser = selectedUsers.find( item => { return selectedValue.object.id == item.value; } );
                            if ( findedUser ) {
                              findedUser.object.isAdmin = value;
                            }
                            setSelectedUsers( structuredClone( selectedUsers ) );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex justify-center w-full h-14 mt-10'>
                  {
                    isAdminOfSector ?
                      <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                        <Button
                          label={ t( 'Delete' ) }
                          icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                          color='#f87171'
                          colorHover='#ef4444'
                          onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                        />
                      </div>
                      :
                      null
                  }
                  <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                    <Button
                      label={ t( 'save' ) }
                      disable={ !canPut }
                      icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                      color='#0bb173'
                      colorHover='#088D5C'
                      onClick={ onSaveClick }
                    />
                  </div>
                </div>
              </form>

              { confirmDeleteModalIsOpen ?
                <ErrorModal
                  title={ t( 'Deletion of {0} !' ).replace( '{0}', sector.name )  }
                  isOpen={ confirmDeleteModalIsOpen }
                  onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
                >
                  <BodyConfirmModal
                    onCancel={ () => { setConfirmDeleteModalIsOpen( false ); } }
                    onConfirm={ async () => {
                      const isDeleted = await deleteSector();
                      if ( isDeleted ) {
                        NavigationService.toSectors( navigate, false );
                      }
                      setConfirmDeleteModalIsOpen( false );
                    } }
                  >
                    <span className='flex flex-col w-full h-full text-[14px] font-[475] text-gray-600 p-3'>
                      { t( 'Are you sure you want to delete this sector ?' ) }
                    </span>
                  </BodyConfirmModal>
                </ErrorModal>
                :
                null
              }
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}